import PropTypes from 'prop-types';
import React from 'react';

import styles from './therapy-soap-modal.css';

const TherapySoapModal = ({
    note,
    loading,
    error,
    creditUsage,
    credits,
    freeScratchSoapNotesUsed,
    onNoteChange,
    onGenerate,
    onCopy,
    onClose
}) => {
    const freeSoapNotesRemaining = Math.max(
        0,
        3 - freeScratchSoapNotesUsed
    );

    const hasFreeSoapNote = freeSoapNotesRemaining > 0;
    const hasAvailableGeneration =
        hasFreeSoapNote || credits >= 1;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div>
                        <h2 className={styles.title}>
                            SOAP Note
                        </h2>

                        <div className={styles.subtitle}>
                            Generated from the therapy activity and session
                            data.
                        </div>

                        <div className={styles.subtitle}>
                            AI may make assumptions based on the targets and
                            information provided.
                        </div>

                        <div className={styles.subtitle}>
                            Please review and edit the note to accurately
                            reflect the session.
                        </div>
                    </div>

                    <button
                        className={styles.closeButton}
                        type="button"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                {!note && !loading && (
                    <div className={styles.emptyState}>
                        <p>
                            Alyd will use the therapy target, trial results,
                            percentages, and session notes to create a concise
                            SOAP note.
                        </p>

                        <p>
                            The student will only be referred to as
                            <strong> “The student.”</strong>
                        </p>

                        {hasFreeSoapNote ? (
                            <p>
                                <strong>
                                    You have {freeSoapNotesRemaining} free SOAP {
                                        freeSoapNotesRemaining === 1 ?
                                            'Note' :
                                            'Notes'
                                    } remaining.
                                </strong>
                                {' '}
                                This generation will use 1 free SOAP Note.
                            </p>
                        ) : credits >= 1 ? (
                            <p>
                                <strong>
                                    This generation will use 1 AI Credit.
                                </strong>
                                {' '}
                                You currently have {credits} AI {
                                    credits === 1 ?
                                        'Credit' :
                                        'Credits'
                                }.
                            </p>
                        ) : (
                            <p>
                                <strong>
                                    You do not have enough AI Credits to
                                    generate a SOAP Note.
                                </strong>
                            </p>
                        )}
                    </div>
                )}

                {loading && (
                    <div className={styles.loading}>
                        Generating SOAP note...
                    </div>
                )}

                {note && !loading && (
                    <textarea
                        className={styles.note}
                        value={note}
                        onChange={event =>
                            onNoteChange(event.target.value)
                        }
                    />
                )}

                {note && !loading && creditUsage && (
                    <div className={styles.creditUsage}>
                        <div className={styles.creditUsageTitle}>
                            {creditUsage.freeUse ?
                                '✨ Free SOAP Note used' :
                                `✨ ${creditUsage.creditsUsed} AI ${
                                    creditUsage.creditsUsed === 1 ?
                                        'Credit' :
                                        'Credits'
                                } used`
                            }
                        </div>

                        <div className={styles.creditUsageDetails}>
                            {
                                creditUsage
                                    .freeScratchSoapNotesRemaining
                            }
                            {' '}free remaining ·{' '}
                            {creditUsage.aiCredits}
                            {' '}AI {
                                creditUsage.aiCredits === 1 ?
                                    'Credit' :
                                    'Credits'
                            } remaining
                        </div>
                    </div>
                )}

                {note && !loading && (
                    <div className={styles.emptyState}>
                        {hasFreeSoapNote ? (
                            <p>
                                Regenerating will use
                                <strong> 1 free SOAP Note</strong>.
                                {' '}
                                {freeSoapNotesRemaining} free {
                                    freeSoapNotesRemaining === 1 ?
                                        'generation remains' :
                                        'generations remain'
                                }.
                            </p>
                        ) : credits >= 1 ? (
                            <p>
                                Regenerating will use
                                <strong> 1 AI Credit</strong>.
                                {' '}
                                You currently have {credits} AI {
                                    credits === 1 ?
                                        'Credit' :
                                        'Credits'
                                }.
                            </p>
                        ) : (
                            <p>
                                <strong>
                                    You do not have enough AI Credits to
                                    regenerate this SOAP Note.
                                </strong>
                            </p>
                        )}
                    </div>
                )}

                <div className={styles.actions}>
                    {!note && (
                        <button
                            className={styles.primaryButton}
                            type="button"
                            disabled={
                                loading ||
                                !hasAvailableGeneration
                            }
                            onClick={onGenerate}
                        >
                            {loading ?
                                'Generating...' :
                                hasFreeSoapNote ?
                                    'Generate SOAP Note — Free' :
                                    credits >= 1 ?
                                        'Use 1 AI Credit' :
                                        'No AI Credits Available'
                            }
                        </button>
                    )}

                    {note && (
                        <>
                            <button
                                className={styles.secondaryButton}
                                type="button"
                                disabled={
                                    loading ||
                                    !hasAvailableGeneration
                                }
                                onClick={onGenerate}
                            >
                                {hasFreeSoapNote ?
                                    'Regenerate — Free' :
                                    credits >= 1 ?
                                        'Regenerate — Use 1 AI Credit' :
                                        'No AI Credits Available'
                                }
                            </button>

                            <button
                                className={styles.primaryButton}
                                type="button"
                                onClick={onCopy}
                            >
                                Copy Note
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

TherapySoapModal.propTypes = {
    note: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.string,
    creditUsage: PropTypes.shape({
        freeUse: PropTypes.bool,
        creditsUsed: PropTypes.number,
        aiCredits: PropTypes.number,
        freeScratchSoapNotesUsed: PropTypes.number,
        freeScratchSoapNotesRemaining: PropTypes.number
    }),
    credits: PropTypes.number,
    freeScratchSoapNotesUsed: PropTypes.number,
    onNoteChange: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

TherapySoapModal.defaultProps = {
    note: '',
    loading: false,
    error: '',
    creditUsage: null,
    credits: 0,
    freeScratchSoapNotesUsed: 0
};

export default TherapySoapModal;